import styled from 'styled-components'
import { SearchProductsRenderer } from '@/features/shop/components/Search/SearchProductsRenderer'
import { Progress, ProgressBar } from '@/features/shared/components/Progress'
import { screenSizes } from '@shipt/design-system-themes'
import { SearchFilterOptions } from '@/features/shop/components/ProductFilter/FilterOptions/SearchFilterOptions'
import { SearchType, type Facet } from '@/features/shop/services/Search/types'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { Body, Display, Headline } from '@shipt/design-system-typography'
import { type useInfiniteQuerySearch } from '@/features/shop/services/Search/queries'
import { type FeaturedPromotion } from '@/features/shop/components/FeaturedPromotionContainer/types'
import { HDivider } from '@/features/shared/components/Dividers'
import { SearchResultsText } from '@/features/shop/components/SearchResultsText'

type Props = {
  headerTitle: string | undefined
  showProgressBar: boolean
  featuredPromotion: FeaturedPromotion | undefined
  infiniteQueryResults: ReturnType<typeof useInfiniteQuerySearch>
  facets: Facet[]
  total_hits: number
}

export const FeaturedPromotionContent = ({
  headerTitle,
  showProgressBar,
  featuredPromotion,
  infiniteQueryResults,
  facets,
  total_hits,
}: Props) => {
  return (
    <Column paddingLeft="lg" spacing="lg">
      <Display size="sm" as="h1">
        {headerTitle ?? ''}
      </Display>
      <Stack direction={{ base: 'column', md: 'row' }} spacing="xxl">
        {!!featuredPromotion?.disclaimerBody && (
          <SubtitleWrapper spacing="md">
            <Body variant="secondary">{featuredPromotion.disclaimerBody}</Body>
          </SubtitleWrapper>
        )}
        <ProductWrapper>
          <Column spacing="lg">
            {showProgressBar && featuredPromotion ? (
              <PromotionProgress>
                <PromotionProgressBar
                  min={0}
                  max={100}
                  value={featuredPromotion.progress}
                  name="featured promotions page"
                />
              </PromotionProgress>
            ) : null}
            <Row align="start" justify="space-between">
              <Headline size="sm" as="span">
                Eligible items
              </Headline>
              <SearchResultsText totalResults={total_hits} />
            </Row>
            <Divider />
            <SearchFilterOptions facets={facets} searchType={SearchType.SEARCH} />
            <SearchProductsRenderer infiniteQueryResults={infiniteQueryResults} />
          </Column>
        </ProductWrapper>
      </Stack>
    </Column>
  )
}

const PromotionProgress = styled(Progress)`
  margin: 0.5rem 0;

  @media ${screenSizes.tablet} {
    margin: 0.5rem 0 0;
  }
`

const Divider = styled(HDivider)`
  margin: 0.5rem 0 0;
`

const SubtitleWrapper = styled(Column)`
  @media ${screenSizes.tablet} {
    width: 35%;
  }
`

const ProductWrapper = styled(Box)`
  @media ${screenSizes.tablet} {
    margin: 0.35rem;
  }
`

const PromotionProgressBar = styled(ProgressBar)`
  background: ${({ theme }) => theme.color.background.base.brand.primary};
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`
